// invest_FE/src/services/discountService.js
import { apiFetch } from '../utils/apiClient';
import { BASE_URL } from '../utils/config';

/**
 * Fetches all discounts for a specific user.
 * @param {string} userId - The ID of the user.
 * @param {string} token - The admin user's auth token.
 * @returns {Promise<Array<object>>} A list of the user's discounts.
 */
export const getUserDiscounts = async (userId, token) => {
  const response = await apiFetch(`${BASE_URL}/api/admin/discounts/user/${userId}`, {}, token);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to fetch user discounts' }));
    throw new Error(errorData.message);
  }
  return response.json();
};

/**
 * Fetches all discounts for the currently logged-in user.
 * @param {string} token - The user's auth token.
 * @returns {Promise<Array<object>>} A list of the user's discounts.
 */
export const getMyDiscounts = async (token) => {
  const response = await apiFetch(`${BASE_URL}/api/admin/discounts/me`, {}, token);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to fetch your discounts' }));
    throw new Error(errorData.message);
  }
  return response.json();
};

/**
 * Creates a new discount for a user.
 * @param {string} userId - The ID of the user.
 * @param {{ productType: string, discountPercentage: number, expiresAt?: string, isActive?: boolean }} discountData - The data for the new discount.
 * @param {string} token - The admin user's auth token.
 * @returns {Promise<object>} The created discount object.
 */
export const createDiscount = async (userId, discountData, token) => {
  const response = await apiFetch(
    `${BASE_URL}/api/admin/discounts/user/${userId}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(discountData),
    },
    token
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to create discount' }));
    throw new Error(errorData.message);
  }
  return response.json();
};

/**
 * Updates an existing discount.
 * @param {string} discountId - The ID of the discount to update.
 * @param {{ discountPercentage?: number, expiresAt?: string, isActive?: boolean }} discountData - The fields to update.
 * @param {string} token - The admin user's auth token.
 * @returns {Promise<object>} The updated discount object.
 */
export const updateDiscount = async (discountId, discountData, token) => {
  const response = await apiFetch(
    `${BASE_URL}/api/admin/discounts/${discountId}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(discountData),
    },
    token
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to update discount' }));
    throw new Error(errorData.message);
  }
  return response.json();
};

/**
 * Deletes a discount.
 * @param {string} discountId - The ID of the discount to delete.
 * @param {string} token - The admin user's auth token.
 * @returns {Promise<void>}
 */
export const deleteDiscount = async (discountId, token) => {
  const response = await apiFetch(`${BASE_URL}/api/admin/discounts/${discountId}`, { method: 'DELETE' }, token);
  // DELETE requests often return 204 No Content, so we check for that.
  if (!response.ok && response.status !== 204) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to delete discount' }));
    throw new Error(errorData.message);
  }
  // No content to return on successful deletion.
};
